import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { storage } from '../../utils/storage';
import AuthLayout from '../common/AuthLayout';
import FeatureList from '../common/FeatureList';

const features = [
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
    title: 'Check Your Inbox',
    description: 'Look for an email from Compliance Hub',
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: 'Click the Link',
    description: 'Click the verification link in the email',
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
    title: 'Get Started',
    description: "You'll be automatically signed in after verification",
  },
];

function EmailVerificationScreen() {
  const navigate = useNavigate();
  const {
    signOut,
    auth0User,
    backendSignInError,
  } = useAuth();
  const { showError } = useToast();

  const userEmail = auth0User?.email || 'your email address';

  const handleBackToLogin = async () => {
    await signOut();
    storage.removeAuth();
    navigate('/signin', { replace: true });
  };

  useEffect(() => {
    if (backendSignInError) {
      showError(backendSignInError);
    }
  }, [backendSignInError, showError]);

  const leftContent = (
    <>
      <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
        Verify Your Email 📧
      </h1>
      <p className="text-lg md:text-xl mb-8 text-gray-700">
        We've sent a verification link to your email address. Click the link
        in the email to complete your registration.
      </p>
      <FeatureList features={features} />
    </>
  );

  const rightContent = (
    <>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-6">
          <svg
            className="w-10 h-10 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          Check Your Email
        </h2>

        <p className="text-base text-gray-600 mb-2">
          We've sent a verification link to
        </p>
        <p className="text-base font-semibold text-gray-900 mb-4 break-all">
          {userEmail}
        </p>
        <p className="text-sm text-gray-500">
          Click the link in the email to verify your account and complete your
          registration.
        </p>
      </div>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Need Help?</span>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={handleBackToLogin}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Back to Sign In
        </button>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600 text-center">
          <strong>Didn't receive the email?</strong> Check your spam folder. 
          The verification link will expire in 24 hours.
        </p>
      </div>
    </>
  );

  return (
    <AuthLayout
      leftContent={leftContent}
      rightContent={rightContent}
      gradientFrom="from-blue-50"
      gradientVia="via-sky-50"
      gradientTo="to-blue-100"
    />
  );
}

export default EmailVerificationScreen;

